import React, { Component } from 'react';
import { connect } from 'react-redux';
import InputAdornment from '@mui/material/InputAdornment';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { TextInput, Services, Button, Text, Theme, i18n, tokens, BubbleSelectBlock } from '@simplybenefits/sb-ui-library';
import Grid from "@mui/material/Grid";

import * as notifier from '../../../actions/notifier.js';
import { updateFormField } from '../../../actions/signup.js';
import { globalStyles } from '../../../theme/index.js';
import Tip from '../../../components/Tip.js';

class EmailPasswordForm extends Component {
    state = {
        userID: '',
        accountID: '',
        language: '',
        email: '',
        password: '',
        confirmPassword: '',
        visibility: false
    };

    componentDidMount() {
        this.setState({ language: this.props.userLanguage });

        if (this.props.userDetails) {
            this.setState({
                userID: this.props.userDetails.userID || this.props.userDetails.userID || '',
                accountID: this.props.userDetails.accountID || this.props.userDetails.accountID || '',
                email: this.props.userDetails.email || '',
                password: this.props.userDetails.password || '',
                confirmPassword: this.props.userDetails.confirmPassword || ''
            });
        }
       
        return null;
    }

    _setLocale = (locale) => {
        this.setState({ 
            language: this.state.language === "fr" ? "en" : "fr",
            userIDError: null,
            accountIDError: null,
            passwordError: null,
            confirmPasswordError: null,
            password: "", 
            confirmPassword: ""
        });

        this.props.toggleLanguage(locale);
    };

    componentDidUpdate(prevProps) {
        if (this.props.userLanguage !== prevProps.userLanguage) {
            this.setState({ 
                language: this.props.userLanguage, 
                password: "", 
                confirmPassword: "" 
            });
        }
    }

    _toggleVisibility = () => {
        this.setState({
            visibility: !this.state.visibility
        });
    };

    // eslint-disable-next-line require-await
    _onBlur = (type) => {
        this.props.dispatch(updateFormField('userDetails', type, this.state[type]));
        return null;
    };

    _handleInputChange = (type, event) => {
        // make the saved state to false, since we have made edits
        this.props.setSavedState('emailPasswordForm', false);

        this.setState({
            [type]: event.target.value
        });
    };

    _saveDetails = async () => {
        if (this.state.password !== this.state.confirmPassword) {
            this.setState({
                confirmPasswordError: "Please be sure that both passwords match"
            });
        }

        const userIDError = await Services.validateInput('userID', this.state.userID);
        const accountIDError = await Services.validateInput('accountID', this.state.accountID);
        const passwordError = await Services.validateInput('password', this.state.password);
        const confirmPasswordError = await Services.validateInput('confirmPassword', this.state.confirmPassword);

        this.setState({
            userIDError,
            accountIDError,
            passwordError,
            confirmPasswordError
        });

        if (
            userIDError ||
            accountIDError ||
            passwordError ||
            confirmPasswordError
        ) {
            this.props.dispatch(notifier.displayError({ verbiage: i18n.t(tokens.labels.pleaseConfirmDetailsError) }));
            return false;
        }

        if (this.state.password !== this.state.confirmPassword) {
            this.props.dispatch(notifier.displayError({ verbiage: i18n.t(tokens.labels.signup.pleaseConfirmThatYourPasswordsMatch) }));

            this.setState({
                confirmPasswordError: i18n.t(tokens.labels.signup.pleaseConfirmThatYourPasswordsMatch),
                passwordError: i18n.t(tokens.labels.signup.pleaseConfirmThatYourPasswordsMatch)
            });

            return false;
        }

        const userDetails = {
            userID: this.state.userID.trim(),
            accountID: this.state.accountID.trim(),
            email: this.state.email.trim(),
            password: this.state.password.trim()
        };

        // make the saved state to true, we have hit next and everything is valid
        this.props.setSavedState('emailPasswordForm', true);
        this.props.stepCompletion({ ...userDetails }, 'userDetails', false);

        return false;
    };

    render() {
        const {
            confirmPassword,
            email,
            password
        } = this.state;

        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column'
            },
            stepperBtnContainer: {
                ...globalStyles.stepperBtnContainer,
                marginTop: 30
            },
            stepperNextBtn: {
                ...globalStyles.stepperNextBtn
            }
        };

        return (
            <div>
                <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.labels.signup.createAccount) }</Text>

                <div style={styles.form}>
                    <Grid container>
                        <Grid item xs={12} sm={12} md={12}>
                            <BubbleSelectBlock
                                customStyle={{ width: "100%" }}
                                first={{
                                    title: "English",
                                    selected: this.state.language === 'en',
                                    field: 'en',
                                    customStyle: { width: "50%" }
                                }}
                                second={{
                                    title: "Français",
                                    selected: this.state.language === 'fr',
                                    field: 'fr',
                                    customStyle: { width: "50%" }
                                }}
                                handleChange={ (field) => this._setLocale(field) }
                            />
                        </Grid>
                    </Grid>

                    <Grid container>
                        <Grid item xs={12} sm={12} md={12}>
                            <TextInput
                                label={ i18n.t(tokens.labels.email) }
                                autoComplete="new-password"
                                value={email}
                                disabled
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Tip
                        left
                        show={ this.props.tips.passwordBreakdown }
                        close={ () => this.props.hideTip('passwordBreakdown') }
                    >
                        { `${ i18n.t(tokens.labels.signup.createAPasswordThatContains) } ` }
                        <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.numbers) }</span>, <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.letters) } </span>
                        { i18n.t(tokens.labels.signup.and) } 
                        <span style={{ fontWeight: 'bold' }}> { i18n.t(tokens.labels.signup.specialCharacters) } </span>
                        { i18n.t(tokens.labels.signup.toMaximizeSecurity) }
                    </Tip>

                    <Grid container>
                        <Grid item xs={12} sm={12} md={12}>
                            <TextInput
                                label={ i18n.t(tokens.labels.password) }
                                value={password}
                                inputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ color: Theme.colors.mainTextGrey, paddingRight: 1 }}>
                                            {
                                                this.state.visibility ?
                                                    <VisibilityIcon
                                                        style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                        onClick={this._toggleVisibility}
                                                    />
                                                    :
                                                    <VisibilityOffIcon
                                                        style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                        onClick={this._toggleVisibility}
                                                    />
                                            }
                                        </InputAdornment>
                                    )
                                }}
                                onBlur={() => this._onBlur('password')}
                                onChange={(value) => this._handleInputChange('password', value)}
                                error={Boolean(this.state.passwordError)}
                                helperText={this.state.passwordError}
                                secure={!this.state.visibility}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Grid container>
                        <TextInput
                            label={ i18n.t(tokens.labels.signup.confirmPassword) }
                            value={confirmPassword}
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ color: Theme.colors.mainTextGrey, paddingRight: 1 }}>
                                        {
                                            this.state.visibility ?
                                                <VisibilityIcon
                                                    style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                    onClick={this._toggleVisibility}
                                                />
                                                :
                                                <VisibilityOffIcon
                                                    style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                    onClick={this._toggleVisibility}
                                                />
                                        }
                                    </InputAdornment>
                                )
                            }}
                            onBlur={() => this._onBlur('confirmPassword')}
                            onChange={(value) => this._handleInputChange('confirmPassword', value)}
                            error={Boolean(this.state.confirmPasswordError)}
                            helperText={this.state.confirmPasswordError}
                            secure={!this.state.visibility}
                            fullWidth
                        />
                    </Grid>

                    <div style={styles.stepperBtnContainer}>
                        <Button
                            style={styles.stepperNextBtn}
                            onClick={this._saveDetails}
                            color="primary"
                        >
                            { i18n.t(tokens.buttons.continue) }
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

EmailPasswordForm.propTypes = {
    language: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    email: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    password: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    confirmPassword: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ])
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    userDetails: state.signup.userDetails
});

export default (connect(mapStateToProps)(EmailPasswordForm));